"use client";

import { formatCanvasFuse, type CanvasFuseLabel } from "./canvas-fuse-label";
import { useCatalogLocale } from "@/components/hooks/use-catalog-locale";
import { resolveImageFilename } from "@/lib/catalog";
import { TERMINAL_COLORS } from "@/lib/category-style";
import { getSimulateStateSpec } from "@/lib/simulate/states";
import { useSystemStore } from "@/lib/store";
import { useProductImageVisibility } from "@/lib/settings/product-image-visibility";
import { catalogImageUrl } from "@/lib/supabase/client";
import type { ComponentDefinition, Terminal, TerminalSide } from "@/lib/types";
import { ComponentProductImage } from "./component-product-image";
import { FuseSymbol } from "@/components/ui/fuse-symbol";
import {
  localizedName,
  localizedShortName,
  localizedSubtitle,
} from "@/lib/i18n";

export function CardBody({
  def,
  displayName,
  bottomSlot,
}: {
  def: ComponentDefinition;
  displayName: string;
  bottomSlot?: React.ReactNode;
}) {
  const locale = useCatalogLocale();
  const subtitle = localizedSubtitle(def, locale);
  const productImagesVisible = useProductImageVisibility((s) => s.visible);
  const imageUrl = productImagesVisible
    ? catalogImageUrl(resolveImageFilename(def))
    : null;
  if (imageUrl) {
    const fullName = localizedName(def, locale);
    // displayName === fullName means the user hasn't set a custom label.
    const shortName = localizedShortName(def, locale);
    const cardLabel = displayName === fullName ? (shortName ?? fullName) : displayName;
    return (
      <div className="component-card--image relative flex flex-col h-full overflow-hidden">
        <div className="flex-none px-3 pt-1.5 pb-1">
          <div
            className="font-semibold text-[12px] tracking-tight truncate text-[var(--color-fg)] leading-tight text-center"
            title={subtitle ? `${fullName} — ${subtitle}` : fullName}
          >
            {cardLabel}
          </div>
        </div>
        <ComponentProductImage key={imageUrl} src={imageUrl} />
        {bottomSlot && <div className="component-card-controls flex-none pb-2">{bottomSlot}</div>}
      </div>
    );
  }
  return (
    <div className="component-card--text flex flex-col h-full px-3 py-2 overflow-hidden">
      <div className="component-card-copy flex flex-col flex-1 justify-center min-h-0">
        {subtitle && (
          <div className="component-card-subtitle text-[10px] text-[var(--color-fg-muted)] truncate leading-tight">
            {subtitle}
          </div>
        )}
        <div
          className="font-semibold text-[13px] tracking-tight line-clamp-2 break-words text-[var(--color-fg)] leading-tight"
          title={localizedName(def, locale)}
        >
          {displayName}
        </div>
      </div>
      {bottomSlot}
    </div>
  );
}

export function SwitchBody({
  nodeId,
  def,
  displayName,
}: {
  nodeId: string;
  def: ComponentDefinition;
  displayName: string;
}) {
  const simulating = useSystemStore((s) => s.mode === "simulate");
  const stateId = useSystemStore(
    (s) => s.simulateState.componentStates[nodeId],
  );
  const setComponentSimulateState = useSystemStore(
    (s) => s.setComponentSimulateState,
  );
  const spec = getSimulateStateSpec(def);
  const activeId = stateId ?? spec?.default;
  // Schematic convention: always drawn open in edit mode; only simulate closes it.
  const closed = simulating && activeId === "on";
  const handleToggle = (e: React.MouseEvent) => {
    if (!simulating || !spec) return;
    e.stopPropagation();
    setComponentSimulateState(nodeId, closed ? "off" : "on");
  };
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 60 30"
      preserveAspectRatio="none"
      style={{ display: "block", overflow: "visible" }}
      aria-label={displayName}
    >
      <line
        x1="9" y1="15" x2="20" y2="15"
        stroke="var(--color-fg)" strokeWidth="1.5" strokeLinecap="round"
      />
      <line
        x1="40" y1="15" x2="51" y2="15"
        stroke="var(--color-fg)" strokeWidth="1.5" strokeLinecap="round"
      />
      <line
        x1="20" y1="15" x2="40" y2={closed ? 15 : 4}
        stroke="var(--color-fg)" strokeWidth="1.5" strokeLinecap="round"
      />
      <circle cx="20" cy="15" r="2" fill="var(--color-fg)" />
      <circle cx="40" cy="15" r="2" fill="var(--color-fg)" />
      {simulating && spec && (
        <rect
          className="nodrag nopan"
          x="0" y="0" width="60" height="30"
          fill="transparent"
          style={{ cursor: "pointer", pointerEvents: "all" }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={handleToggle}
        >
          <title>{closed ? "On — click to turn off" : "Off — click to turn on"}</title>
        </rect>
      )}
    </svg>
  );
}

export function FuseHolderBody({
  def,
  fuses,
  displayName,
  holderFuseAmp,
}: {
  def: ComponentDefinition;
  fuses?: Record<string, CanvasFuseLabel>;
  displayName: string;
  holderFuseAmp?: number | null;
}) {
  const outputTerm = def.terminals.find((t) => t.direction === "output");
  // A declared holder is independent of the cable's automatic setting.
  const fuse = holderFuseAmp != null && holderFuseAmp > 0
    ? { rating: holderFuseAmp, review: undefined }
    : outputTerm ? fuses?.[outputTerm.id] : undefined;
  const rating = fuse?.rating;
  const family = def.fuseSlotType ?? "";
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 60 40"
      preserveAspectRatio="none"
      style={{ display: "block", overflow: "visible" }}
      aria-label={displayName}
    >
      {/* The centre must stay at y=20 to match the terminal offset (0.5 of a
       *  40-unit body), or the leads miss the studs. */}
      {family && (
        <text
          x="30"
          y="6"
          textAnchor="middle"
          fontSize="6"
          fontWeight="700"
          letterSpacing="0.04em"
          fill="var(--color-fg-muted)"
        >
          {family}
        </text>
      )}
      <line
        x1="0" y1="20" x2="14" y2="20"
        stroke="var(--color-fg)" strokeWidth="1.5" strokeLinecap="round"
      />
      <line
        x1="46" y1="20" x2="60" y2="20"
        stroke="var(--color-fg)" strokeWidth="1.5" strokeLinecap="round"
      />
      {/* Fuse body (IEC 60617). */}
      <rect
        x="14" y="12" width="32" height="16" rx="3" ry="3"
        fill="var(--color-surface)"
        stroke="var(--color-fg)" strokeWidth="1.25"
      />
      <line
        x1="14" y1="20" x2="46" y2="20"
        stroke="var(--color-fg)" strokeWidth="1.25"
      />
      <text
        x="30"
        y={rating != null ? 17.5 : 18.5}
        textAnchor="middle"
        fontSize={rating != null ? "7" : "8"}
        fontWeight="700"
        fill={rating != null ? "var(--color-fg)" : "var(--color-fg-muted)"}
      >
        {rating != null ? `${rating}A` : "—"}
      </text>
      {fuse?.review && <text x="30" y="37" textAnchor="middle" fontSize="6" fill="var(--color-fg)">{fuse.review}</text>}
    </svg>
  );
}

export function BusbarBody({
  def,
  terms,
  dims,
}: {
  def: ComponentDefinition;
  displayName: string;
  terms: Terminal[];
  dims: { width: number; height: number };
}) {
  const locale = useCatalogLocale();
  const ariaLabel = localizedShortName(def, locale) ?? localizedName(def, locale);
  const polarity = def.terminals.every((t) => t.polarity === "positive")
    ? "positive"
    : def.terminals.every((t) => t.polarity === "negative")
      ? "negative"
      : "neutral";
  const color =
    polarity === "positive"
      ? TERMINAL_COLORS.positive
      : polarity === "negative"
        ? TERMINAL_COLORS.negative
        : "var(--color-fg)";

  const W = dims.width;
  const H = dims.height;

  const sideCounts: Record<TerminalSide, number> = { left: 0, right: 0, top: 0, bottom: 0 };
  for (const t of terms) sideCounts[t.side] += 1;
  const sides: TerminalSide[] = ["left", "right", "top", "bottom"];
  let outputsSide: TerminalSide = sides[0];
  for (const s of sides) {
    if (sideCounts[s] > sideCounts[outputsSide]) outputsSide = s;
  }
  const feedSide = sides.find((s) => s !== outputsSide && sideCounts[s] > 0) ?? null;
  const feedTerm = feedSide ? terms.find((t) => t.side === feedSide) ?? null : null;

  const isVertical = outputsSide === "left" || outputsSide === "right";
  const ROD = 3.5;
  const PAD = 6;

  let rodX1: number, rodY1: number, rodX2: number, rodY2: number;
  if (outputsSide === "right") {
    rodX1 = rodX2 = W;
    rodY1 = PAD;
    rodY2 = H - PAD;
  } else if (outputsSide === "left") {
    rodX1 = rodX2 = 0;
    rodY1 = PAD;
    rodY2 = H - PAD;
  } else if (outputsSide === "top") {
    rodX1 = PAD;
    rodX2 = W - PAD;
    rodY1 = rodY2 = 0;
  } else {
    rodX1 = PAD;
    rodX2 = W - PAD;
    rodY1 = rodY2 = H;
  }

  let stub: { x1: number; y1: number; x2: number; y2: number } | null = null;
  if (feedTerm && feedSide) {
    let fx: number, fy: number;
    if (feedSide === "left") {
      fx = 0;
      fy = feedTerm.offset * H;
    } else if (feedSide === "right") {
      fx = W;
      fy = feedTerm.offset * H;
    } else if (feedSide === "top") {
      fx = feedTerm.offset * W;
      fy = 0;
    } else {
      fx = feedTerm.offset * W;
      fy = H;
    }
    if (isVertical) {
      stub = { x1: fx, y1: fy, x2: outputsSide === "right" ? W : 0, y2: fy };
    } else {
      stub = { x1: fx, y1: fy, x2: fx, y2: outputsSide === "bottom" ? H : 0 };
    }
  }

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      style={{ display: "block", overflow: "visible", position: "absolute", inset: 0 }}
      aria-label={ariaLabel}
    >
      {stub && (
        <line
          x1={stub.x1}
          y1={stub.y1}
          x2={stub.x2}
          y2={stub.y2}
          stroke={color}
          strokeOpacity={0.85}
          strokeWidth={1.5}
          strokeLinecap="round"
        />
      )}
      <line
        x1={rodX1}
        y1={rodY1}
        x2={rodX2}
        y2={rodY2}
        stroke={color}
        strokeWidth={ROD}
        strokeLinecap="round"
      />
    </svg>
  );
}

export function FuseboxBody({
  def,
  terms,
  fuses,
  rotation,
}: {
  def: ComponentDefinition;
  terms: Terminal[];
  /** Fuse rating per terminal id. */
  fuses: Record<string, CanvasFuseLabel> | undefined;
  rotation: 0 | 90 | 180 | 270;
}) {
  const locale = useCatalogLocale();
  const ariaLabel = localizedShortName(def, locale) ?? localizedName(def, locale);

  const sideCounts: Record<TerminalSide, number> = { left: 0, right: 0, top: 0, bottom: 0 };
  for (const t of terms) sideCounts[t.side] += 1;
  const sides: TerminalSide[] = ["left", "right", "top", "bottom"];
  let outputsSide: TerminalSide = sides[0];
  for (const s of sides) {
    if (sideCounts[s] > sideCounts[outputsSide]) outputsSide = s;
  }
  const studs = terms.filter((t) => t.side === outputsSide);

  const PILL_INSET = 14;
  const pillStyleAt = (offset: number): React.CSSProperties => {
    if (outputsSide === "right") {
      return {
        right: PILL_INSET,
        top: `${offset * 100}%`,
        transform: "translate(0, -50%)",
      };
    }
    if (outputsSide === "left") {
      return {
        left: PILL_INSET,
        top: `${offset * 100}%`,
        transform: "translate(0, -50%)",
      };
    }
    if (outputsSide === "bottom") {
      return {
        bottom: PILL_INSET,
        left: `${offset * 100}%`,
        transform: "translate(-50%, 0)",
      };
    }
    return {
      top: PILL_INSET,
      left: `${offset * 100}%`,
      transform: "translate(-50%, 0)",
    };
  };

  return (
    <div
      className="relative w-full h-full"
      aria-label={ariaLabel}
      style={{ overflow: "hidden", borderRadius: 11 }}
    >
      {studs.map((t) => {
        const fuse = fuses?.[t.id];
        const hasRating = fuse != null;
        return (
          <div
            key={t.id}
            style={{
              position: "absolute",
              ...pillStyleAt(t.offset),
              pointerEvents: "none",
            }}
          >
            <div
              className="inline-flex items-center gap-1 px-1.5 py-[1px] rounded-[5px] tabular-nums"
              style={{
                background: hasRating
                  ? "var(--color-surface)"
                  : "var(--color-surface-2)",
                border: `1px solid ${hasRating ? "var(--color-border)" : "var(--color-fg-subtle)"}`,
                color: hasRating
                  ? "var(--color-cat-distribution)"
                  : "var(--color-fg-subtle)",
                fontSize: 10,
                fontWeight: 600,
                lineHeight: 1.2,
                letterSpacing: "0.02em",
                whiteSpace: "nowrap",
                boxShadow: "var(--shadow-sm)",
                transform: rotation ? `rotate(${-rotation}deg)` : undefined,
                transformOrigin: "center center",
              }}
            >
              <FuseSymbol width={11} height={6} />
              <span>{fuse ? formatCanvasFuse(fuse) : "—"}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function BatteryFusePills({
  terms,
  ratingA,
  rotation,
}: {
  terms: Terminal[];
  ratingA: number;
  rotation: 0 | 90 | 180 | 270;
}) {
  const PILL_INSET = 10;
  const pillStyleAt = (t: Terminal): React.CSSProperties => {
    if (t.side === "left") {
      return { left: PILL_INSET, top: `${t.offset * 100}%`, transform: "translate(0, -50%)" };
    }
    if (t.side === "right") {
      return { right: PILL_INSET, top: `${t.offset * 100}%`, transform: "translate(0, -50%)" };
    }
    if (t.side === "top") {
      return { top: PILL_INSET, left: `${t.offset * 100}%`, transform: "translate(-50%, 0)" };
    }
    return { bottom: PILL_INSET, left: `${t.offset * 100}%`, transform: "translate(-50%, 0)" };
  };
  return (
    <div
      className="absolute inset-0"
      style={{ pointerEvents: "none", overflow: "hidden", borderRadius: 11 }}
      aria-hidden
    >
      {terms
        .filter((t) => t.polarity === "positive")
        .map((t) => (
          <div key={t.id} style={{ position: "absolute", ...pillStyleAt(t) }}>
            <div
              className="inline-flex items-center gap-1 px-1.5 py-[1px] rounded-[5px] tabular-nums"
              style={{
                background: "var(--color-surface)",
                border: "1px solid var(--color-border)",
                color: "var(--color-cat-distribution)",
                fontSize: 10,
                fontWeight: 600,
                lineHeight: 1.2,
                letterSpacing: "0.02em",
                whiteSpace: "nowrap",
                boxShadow: "var(--shadow-sm)",
                transform: rotation ? `rotate(${-rotation}deg)` : undefined,
                transformOrigin: "center center",
              }}
            >
              <FuseSymbol width={11} height={6} />
              <span>{ratingA}A</span>
            </div>
          </div>
        ))}
    </div>
  );
}

export function ChassisGroundBody({ displayName }: { displayName: string }) {
  // IEC 60417-5017 chassis-ground glyph.
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 28 28"
      preserveAspectRatio="xMidYMid meet"
      style={{ display: "block", overflow: "visible" }}
      aria-label={displayName}
    >
      <line
        x1="14"
        y1="0"
        x2="14"
        y2="13"
        stroke="var(--color-fg)"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <line
        x1="4"
        y1="14"
        x2="24"
        y2="14"
        stroke="var(--color-fg)"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
      <line
        x1="7"
        y1="19"
        x2="21"
        y2="19"
        stroke="var(--color-fg)"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <line
        x1="10"
        y1="24"
        x2="18"
        y2="24"
        stroke="var(--color-fg)"
        strokeWidth="1.25"
        strokeLinecap="round"
      />
    </svg>
  );
}
